import { AnyObject } from '@store/types';
import React from 'react';
import { observer, inject } from 'mobx-react';
import { observable, makeObservable } from 'mobx';
import autobind from 'autobind';
import { v4 as uuid } from 'uuid';

import { Nullable } from 'chaika-frontend-types/frontend-cms';

import { EhrApi } from '@api/EhrApi';

import { ProfileForm } from '@profile/store/ProfileForm';
import { Ehr } from '@patient/store/ehr/Ehr';
import { AppState } from '@store/App/App';

import { DateFormat, DateHelper } from '@utils/DateHelper';

import { DatePicker, DatePickerProps } from '@components/datePickers';

interface Props extends Partial<DatePickerProps> {
    name: string;
    noopDate?: boolean;
    validIfNotEmpty?: boolean;
    maxDate?: Date;
}

interface StoreProps {
    ehr: Ehr;
    profileForm: ProfileForm;
    app: AppState;
}

@observer
class Component extends React.Component<Props & StoreProps> {
    @observable
    private showValidStatus: boolean = false;

    constructor(props: Props & StoreProps) {
        super(props);
        makeObservable(this);
    }

    public render(): React.ReactNode {
        const { name, profileForm, validIfNotEmpty, ...props } = this.props;

        const value = profileForm.getFieldValue<string | number>(name !== 'age' ? name : 'ageString') || '';
        const isValid = profileForm.isFieldValid(name);

        const showValidStatus: boolean = validIfNotEmpty
            ? Boolean(this.showValidStatus && value && value !== '')
            : this.showValidStatus;

        return React.createElement(DatePicker, {
            ...props,
            name,
            isValid,
            showValidStatus,
            selected: value ? new Date(value) : undefined,
            onFocus: this.handleFocus,
            onBlur: this.handleBlur,
            onChange: this.handleDateChange,
        });
    }

    @autobind
    protected async handleDateChange(newDate: Date | string) {
        await this.updateFieldValue(newDate as string);
    }

    @autobind
    protected async handleFocus() {
        this.showValidStatus = true;
    }

    @autobind
    protected async handleBlur() {
        this.showValidStatus = false;
    }

    private async updateFieldValue(value: Nullable<string>) {
        const { ehr, name, profileForm, app, noopDate } = this.props;
        const reqId = uuid();

        const oldValue = profileForm.getFormattedFieldValue(name);

        profileForm.setFieldValue(name, value);
        const newValue = profileForm.getFormattedFieldValue(name);

        if (profileForm.isFieldValid(name)) {
            this.showValidStatus = false;
        }

        if (oldValue !== newValue && profileForm.isFieldValid(name)) {
            app.addSaveRequest(reqId);

            const newValue = noopDate
                ? value === null
                    ? null
                    : DateHelper.format(value, DateFormat.DateWithDashSeparator)
                : value;
            await EhrApi.update(ehr.id, { [name]: newValue });

            (ehr as AnyObject)[name] = newValue;

            app.removeSaveRequest(reqId);
        }
    }
}

export const DateInputContainer = inject<Props, StoreProps>(Ehr.Name, ProfileForm.Name, AppState.Name)(Component);
